import React, { useState, useEffect, forwardRef } from "react";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import { formatDateToUser } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import editIcon from "../assets/Images/Edit.png";
import viewIcon from "../assets/Images/View.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import crossIcon from "../assets/icons/crossIcon.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../utils/formatDate";
import subscriptionIcon from "../assets/icons/SubscriptionIcon.png";
import CheckboxGroup from "./components/CheckboxGroup";

const SUBSCRIPTION_API_URL = `${process.env.REACT_APP_API_URL}/subscriptions`;
const Product_API_URL = `${process.env.REACT_APP_API_URL}/products`;

const SubscriptionManagement = () => {
  const [products, setProducts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);

  //set default checkbox value true
  const defaultCheckboxGroup = (items) =>
    items.reduce((acc, item) => ({ ...acc, [item]: true }), {});

  //intiall from data
  const getInitialFormData = () => ({
    subscription_name: "",
    subscription_description: "",
    product_name: "",
    payment_freq: "Monthly",
    allowed_user: "1",
    amount: "",
    plan_type: "Basic",
    start_date: "",
    end_date: "",
    subscription_status: "1",
    api_count: 200,
    features: {
      server_hosting: {
        Server_Info: false,
        Server_Location: false,
        Open_Ports: false,
        Traceroute: false,
        Ports_Info: false,
        IP_Info: false,
        My_Traceroute: false,
      },

      Website_performance: {
        Global_Ranking: false,
        Quality_Metrics: false,
        Redirect_Chain: false,
        Server_Status: false,
        Legacy_Rank: false,
        Device_Summary: false,
      },
      Tracking_data_collection: {
        Cookies: false,
        Social_Tags: false,
        Linked_Pages: false,
        Listed_Pages: false,
        Features: false,
      },
      Web_Crawling_configuration: {
        Crawl_Rules: false,
        Email_Configuration: false,
        Archives: false,
        Carbon_Footprint: false,
        Headers: false,
        Robot_Txt: false,
        Sitemap: false,
        The_Harvester: false,
        Threats: false,
        Mail_Config: false,
      },
      Domian_DNS_Information: {
        Associated_Hosts: false,
        DNS_Records: false,
        DNS_Security_Extensions: false,
        DNS_Server: false,
        Domian_Info: false,
        TXT_Records: false,
        Whois_Lookup: false,
      },
      Website_Security_protection: {
        Block_Detection: false,
        Firewall_Detection: false,
        Malware_and_Phishing_Detection: false,
        Security: false,
        HTTP_Security_Features: false,
        HTTP_Strict_Transport_Security: false,
        SSL_Chain: false,
        SSL_Info: false,
        TLS_Cipher_Suites: false,
      },
    },
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [subscriptionStats, setSubscriptionStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    expired: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    console.log("Sorting by:", sortOrder);
    console.log("Filtering by day:", dayFilter);
  };

  const fetchSubscriptions = async () => {
    if (!token) {
      showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);
      return;
    }
    try {
      const response = await api.get(SUBSCRIPTION_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const response2 = await api.get(Product_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSubscriptions(response.data);
      setProducts(response2.data);
    } catch (error) {
      console.error("Failed to fetch subscriptions", error);
      showErrorToast(TOAST_MESSAGES.SUBSCRIPTION.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const filteredSubscriptions = subscriptions
    .filter((subscription) => {
      const lower = searchTerm.toLowerCase();
      const subscriptionName = subscription.subscription_name
        ? subscription.subscription_name.toLowerCase()
        : "";
      const subscriptionDesc = subscription.subscription_description
        ? subscription.subscription_description.toLowerCase()
        : "";
      return (
        subscriptionName.includes(lower) || subscriptionDesc.includes(lower)
      );
    })
    .filter((subscription) => {
      if (!dayFilter) return true;
      const subscriptionDate = new Date(subscription.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return subscriptionDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return subscriptionDate >= yesterday && subscriptionDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredSubscriptions.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredSubscriptions.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscription?"))
      return;
    try {
      await api.delete(`${SUBSCRIPTION_API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchSubscriptions();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const parseJSONField = (field, defaultObj) => {
    if (!field) return { ...defaultObj };
    try {
      return typeof field === "string" ? JSON.parse(field) : field;
    } catch {
      return { ...defaultObj };
    }
  };

  const handleEdit = (subscription) => {
    const defaults = getInitialFormData().features; // your default feature structure

    setFormData({
      subscription_name: subscription.subscription_name,
      subscription_description: subscription.subscription_description || "",
      product_name: subscription.product_name ?? "",
      payment_freq: subscription.payment_freq ?? "Monthly",
      allowed_user: subscription.allowed_user ?? "1",
      amount: subscription.subscription_amount ?? "",
      plan_type: subscription.subscription_plan_type || "Basic",
      start_date: subscription.subscription_start_date?.slice(0, 10) || "",
      end_date: subscription.subscription_end_date?.slice(0, 10) || "",
      subscription_status: subscription.subscription_status || "1",
      api_count: subscription.api_count || 200,
      // merge defaults with DB values
      features: {
        server_hosting: {
          ...defaults.server_hosting,
          ...(subscription.features?.server_hosting || {}),
        },
        Website_performance: {
          ...defaults.Website_performance,
          ...(subscription.features?.Website_performance || {}),
        },
        Tracking_data_collection: {
          ...defaults.Tracking_data_collection,
          ...(subscription.features?.Tracking_data_collection || {}),
        },
        Web_Crawling_configuration: {
          ...defaults.Web_Crawling_configuration,
          ...(subscription.features?.Web_Crawling_configuration || {}),
        },
        Domian_DNS_Information: {
          ...defaults.Domian_DNS_Information,
          ...(subscription.features?.Domian_DNS_Information || {}),
        },
        Website_Security_protection: {
          ...defaults.Website_Security_protection,
          ...(subscription.features?.Website_Security_protection || {}),
        },
      },
    });

    setEditingSubscriptionId(subscription.subscription_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (subscription) => {
    setFormData({
      subscription_name: subscription.subscription_name,
      subscription_description: subscription.subscription_description || "",
      product_name: subscription.product_name ?? "",
      payment_freq: subscription.payment_freq ?? "Monthly",
      allowed_user: subscription.allowed_user ?? "1",
      amount: subscription.subscription_amount ?? "",
      plan_type: subscription.subscription_plan_type || "Basic",
      start_date: subscription.subscription_start_date?.slice(0, 10) || "",
      end_date: subscription.subscription_end_date?.slice(0, 10) || "",
      api_count: subscription.api_count || 200,
      // 🔑 merge defaults with db features
      features: {
        server_hosting: subscription.features?.server_hosting || {},
        Website_performance: subscription.features?.Website_performance || {},
        Tracking_data_collection:
          subscription.features?.Tracking_data_collection || {},
        Web_Crawling_configuration:
          subscription.features?.Web_Crawling_configuration || {},
        Domian_DNS_Information:
          subscription.features?.Domian_DNS_Information || {},
        Website_Security_protection:
          subscription.features?.Website_Security_protection || {},
      },
    });

    setEditingSubscriptionId(subscription.subscription_id);
    setViewMode(true);
    setShowForm(true);
  };

  const handleChangeStatus = async (subscription) => {
    const current = subscription.subscription_status;
    let newStatus;

    if (current === "1") newStatus = "0";
    else if (current === "0") newStatus = "1";
    else {
      showErrorToast("Cannot change status of Expired subscription");
      return;
    }

    const statusText = newStatus === "1" ? "Active" : "Inactive";

    try {
      await api.put(
        `${SUBSCRIPTION_API_URL}/change-status/${subscription.subscription_id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchSubscriptions();
      fetchStats();

      showSuccessToast(
        `${TOAST_MESSAGES.SUBSCRIPTION.STATUS_CHANGE_SUCCESS} to ${statusText}`
      );
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.SUBSCRIPTION.STATUS_CHANGE_ERROR);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`${SUBSCRIPTION_API_URL}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionStats(res.data);
    } catch (err) {
      console.error("Failed to fetch subscription stats:", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (showForm && !editingSubscriptionId) {
      const today = new Date().toISOString().split("T")[0];
      setFormData((prev) => ({ ...prev, start_date: today }));
    }
  }, [showForm, editingSubscriptionId]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      showErrorToast("End date must be after start date.");
      return;
    }

    setIsSubmitting(true); // 👈 Start loading

    const payload = {
      subscription_name: formData.subscription_name,
      subscription_description: formData.subscription_description,
      product_name: formData.product_name,
      payment_freq: formData.payment_freq,
      allowed_user: formData.allowed_user,
      subscription_amount: parseFloat(formData.amount),
      subscription_plan_type: formData.plan_type,
      subscription_start_date: formData.start_date,
      subscription_end_date: formData.end_date,
      subscription_status: formData.subscription_status || "1",
      api_count: formData.api_count || "200",
      features: formData.features, // ✅ send features
    };

    try {
      const url = editingSubscriptionId
        ? `${SUBSCRIPTION_API_URL}/${editingSubscriptionId}`
        : SUBSCRIPTION_API_URL;
      const method = editingSubscriptionId ? "PUT" : "POST";

      await api({
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      });

      setFormData(getInitialFormData());
      setShowForm(false);
      setEditingSubscriptionId(null);
      fetchSubscriptions();
      showSuccessToast(
        editingSubscriptionId
          ? TOAST_MESSAGES.SUBSCRIPTION.UPDATE_SUCCESS
          : TOAST_MESSAGES.SUBSCRIPTION.CREATE_SUCCESS
      );
    } catch (err) {
      console.error("Error creating/updating subscription:", err);
      showErrorToast(
        editingSubscriptionId
          ? TOAST_MESSAGES.SUBSCRIPTION.UPDATE_ERROR
          : TOAST_MESSAGES.SUBSCRIPTION.CREATE_ERROR
      );
    } finally {
      setIsSubmitting(false); // 👈 Stop loading
    }
  };

  const closeModal = () => {
    setShowForm(false);

    setFormData(getInitialFormData());
    setEditingSubscriptionId(null);
    setViewMode(false);
  };

  const exportExcel = () => {
    const wsData = [
      [
        "Subscription Name",
        "Description",
        "Plan Type",
        "Amount",
        "Payment Frequency",
        "API Count",
        "Allowed Users",
        "Start Date",
        "End Date",
        "Status",
        "Updated Timestamp",
      ],
      ...filteredSubscriptions.map((p) => [
        p.subscription_name || "",
        p.subscription_description || "",
        p.subscription_plan_type || "",
        p.subscription_amount || 0,
        p.payment_freq || "",
        p.api_count || 0,
        p.allowed_user || "",
        p.subscription_start_date
          ? new Date(p.subscription_start_date).toLocaleDateString()
          : "",
        p.subscription_end_date
          ? new Date(p.subscription_end_date).toLocaleDateString()
          : "",
        p.subscription_status === "1"
          ? "Active"
          : p.subscription_status === "0"
          ? "Inactive"
          : "Expired",
        p.updated_timestamp
          ? new Date(p.updated_timestamp).toLocaleString()
          : "",
      ]),
    ];

    // Create and save workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Subscriptions");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "subscription_management.xlsx");
  };

  // console.log(formData);

  const updateFeatureGroup = (prevGroup, updates, allKeys) => {
    const merged = { ...prevGroup, ...updates };
    // ensure all keys exist
    allKeys.forEach((key) => {
      if (!(key in merged)) merged[key] = false;
    });
    return merged;
  };

  return (
    <div className="mx-2 pt-1  h-100">
      <div className="d-flex flex-wrap gap-3 mb-2 justify-content-evenly align-items-center">
        {/* Total Subscription */}
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Total"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Total Subscription</h6>
          </div>
          <h2 className="fw-bold">{subscriptionStats.total}</h2>
        </div>

        {/* Active Subscriptions */}
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Active"
              className="icon-image1"
              style={{
                width: "22px",
                height: "22px",
              }}
            />
            <h6 className="ms-2 mb-0">Active Subscriptions</h6>
          </div>
          <h2 className="fw-bold">{subscriptionStats.active}</h2>
        </div>

        {/* Inactive Subscriptions */}
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Inactive"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Inactive Subscriptions</h6>
          </div>
          <h2 className="fw-bold">{subscriptionStats.inactive}</h2>
        </div>

        {/* Expired Subscriptions */}
        <div className="dashboard-box p-3 rounded shadow-sm">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Expired"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Expired Subscription</h6>
          </div>
          <h2 className="fw-bold ">{subscriptionStats.expired}</h2>
        </div>
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Subscription Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Subscriptions"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="btn icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts px-0">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bolder refresh-prompts px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Subscription"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table subscription-table1 table-sm w-100">
              <thead>
                <tr>
                  <th>Subscription Name</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Plan Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {currentData.map((s) => (
                  <tr key={s.subscription_id}>
                    <td
                      title={s.subscription_name}
                      style={{
                        maxWidth: "190px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {s.subscription_name
                        ? s.subscription_name.charAt(0).toUpperCase() +
                          s.subscription_name.slice(1)
                        : "—"}
                    </td>
                    <td title={s.subscription_description}>
                      {s.subscription_description || "—"}
                    </td>
                    <td>{s.subscription_amount}</td>
                    <td>{s.subscription_plan_type}</td>
                    <td>{formatDateToUser(s.subscription_start_date)}</td>
                    <td>{formatDateToUser(s.subscription_end_date)}</td>
                    <td>
                      {s.subscription_status === "1"
                        ? "Active"
                        : s.subscription_status === "0"
                        ? "Inactive"
                        : "Expired"}
                    </td>
                    <td>{formatDateToUserTimeZone(s.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(s)}
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(s)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(s)}
                        title={
                          s.subscription_status === "2"
                            ? "Expired subscriptions cannot change status"
                            : "Change Status"
                        }
                        disabled={s.subscription_status === "2"}
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* <div className="d-flex justify-content-between align-items-center mt-2 table-footer"> */}
          <div className="table-footer fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleFormSubmit}>
                {/* Modal Header */}
                <div className="modal-header  justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewMode
                      ? "View Subscription"
                      : editingSubscriptionId
                      ? "Update Subscription"
                      : "Add Subscription"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-4 mb-2 ">
                      <label className="form-label">Product Name</label>
                      <select
                        className="form-select"
                        value={formData.product_name || ""}
                        onChange={(e) => {
                          const selectedProduct = e.target.value;
                          setFormData({
                            ...formData,
                            product_name: selectedProduct,
                            // Update subscription_name based on selected product and plan type
                            subscription_name:
                              selectedProduct + " " + formData.plan_type,
                          });
                        }}
                        required
                        disabled={viewMode}
                      >
                        <option value="Select Plan Type">
                          Select Plan Type
                        </option>
                        {products.map((product) => (
                          <option
                            key={product.product_id}
                            value={product.product_name}
                          >
                            {product.product_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-2 ">
                      <label className="form-label">Subscription Type</label>
                      <select
                        className="form-select"
                        value={formData.plan_type || ""}
                        onChange={(e) => {
                          const selectedPlan = e.target.value;
                          setFormData({
                            ...formData,
                            plan_type: selectedPlan,
                            // Update subscription_name based on selected product and plan type
                            subscription_name:
                              formData.product_name + " " + selectedPlan,
                          });
                        }}
                        required
                        disabled={viewMode}
                      >
                        <option value="Select Plan Type">
                          Select Plan Type
                        </option>
                        <option value="Basic">Basic</option>
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                      </select>
                    </div>

                    <div className="col-md-4 mb-2">
                      <label className="form-label">Subscription Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.subscription_name || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subscription_name: e.target.value,
                          })
                        }
                        required
                        disabled
                      />
                    </div>

                    <div className="col-md-4 mb-2">
                      <label className="form-label">Amount ($)</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({ ...formData, amount: e.target.value })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <label className="form-label">Payment Frequency</label>
                      <select
                        className="form-select"
                        value={formData.payment_freq}
                        onChange={(e) => {
                          const value = e.target.value;
                          let endDate = formData.end_date;

                          if (value === "Life_Time") {
                            endDate = ""; // clear end date
                          } else if (
                            value === "Monthly" &&
                            formData.start_date
                          ) {
                            const start = new Date(formData.start_date);
                            start.setMonth(start.getMonth() + 1);
                            endDate = start.toLocaleDateString("en-CA");
                          } else if (
                            value === "Annually" &&
                            formData.start_date
                          ) {
                            const start = new Date(formData.start_date);
                            start.setFullYear(start.getFullYear() + 1);
                            endDate = start.toLocaleDateString("en-CA");
                          }

                          setFormData({
                            ...formData,
                            payment_freq: value,
                            end_date: endDate,
                          });
                        }}
                        required
                        disabled={viewMode}
                      >
                        <option value="">Select Payment Frequency</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annually">Annually</option>
                        {/* <option value="Life_Time">Life Time</option> */}
                      </select>
                    </div>

                    <div className="col-md-4 mb-2">
                      <label className="form-label">Allowed User</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={formData.allowed_user}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            allowed_user: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-2 mb-2">
                      <label className="form-label">Start Date</label>
                      <DatePicker
                        className="datecolor"
                        calendarClassName="datecolor"
                        selected={
                          formData.start_date
                            ? new Date(formData.start_date)
                            : null
                        }
                        onChange={(date) => {
                          // const iso = date?.toISOString().split("T")[0];
                          const iso = date.toLocaleDateString("en-CA");
                          setFormData((prev) => ({
                            ...prev,
                            start_date: iso,
                            end_date: prev.end_date < iso ? "" : prev.end_date,
                          }));
                        }}
                        dateFormat="MM/dd/yyyy"
                        minDate={new Date()}
                        customInput={<CustomDateInput />}
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-2">
                      <label className="form-label">End Date</label>
                      <DatePicker
                        selected={
                          formData.end_date ? new Date(formData.end_date) : null
                        }
                        onChange={(date) => {
                          const iso = date?.toLocaleDateString("en-CA");
                          setFormData((prev) => ({ ...prev, end_date: iso }));
                        }}
                        dateFormat="MM/dd/yyyy"
                        minDate={
                          formData.start_date
                            ? new Date(formData.start_date)
                            : new Date()
                        }
                        customInput={<CustomDateInput />}
                        disabled={
                          formData.payment_freq === "Life_Time" || viewMode
                        }
                      />
                    </div>

                    {/* <div className="col-md-4 mb-2 mb-2">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.subscription_status || "1"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subscription_status: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                        <option value="2">Expired</option>
                      </select>
                    </div> */}

                    <div className="col-md-4 mb-2 mb-2">
                      <label className="form-label">API count</label>
                      <input
                        type="number"
                        className="form-control"
                        min="200"
                        value={formData.api_count}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            api_count: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="input-textarea1"
                      rows="2"
                      value={formData.subscription_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subscription_description: e.target.value,
                        })
                      }
                      disabled={viewMode}
                    />
                  </div>

                  {/* Checkboxes */}
                  <div>
                    <span>Services</span>
                    <div className="row" style={{ padding: "1rem" }}>
                      <div className="col-12 col-md-6">
                        <div className="col-12 mb-3">
                          <CheckboxGroup
                            groupLabel="Server & Hosting Information"
                            items={[
                              "Server_Info",
                              "Server_Location",
                              "Open_Ports",
                              "Traceroute",
                              "Ports_Info",
                              "IP_Info",
                              "My_Traceroute",
                            ]}
                            values={formData.features.server_hosting}
                            onChange={(updates) =>
                              setFormData((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  server_hosting: updateFeatureGroup(
                                    prev.features.server_hosting,
                                    updates,
                                    [
                                      "Server_Info",
                                      "Server_Location",
                                      "Open_Ports",
                                      "Traceroute",
                                      "Ports_Info",
                                      "IP_Info",
                                      "My_Traceroute",
                                    ]
                                  ),
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-12 mb-3">
                          <CheckboxGroup
                            groupLabel="Website Performance & Quality Metrics"
                            items={[
                              "Global_Ranking",
                              "Quality_Metrics",
                              "Redirect_Chain",
                              "Server_Status",
                              "Legacy_Rank",
                              "Device_Summary",
                            ]}
                            values={formData.features.Website_performance}
                            onChange={(updates) =>
                              setFormData((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  Website_performance: updateFeatureGroup(
                                    prev.features.Website_performance,
                                    updates,
                                    [
                                      "Global_Ranking",
                                      "Quality_Metrics",
                                      "Redirect_Chain",
                                      "Server_Status",
                                      "Legacy_Rank",
                                      "Device_Summary",
                                    ]
                                  ),
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-12 mb-3">
                          <CheckboxGroup
                            groupLabel="Tracking & Data Collection"
                            items={[
                              "Cookies",
                              "Social_Tags",
                              "Linked_Pages",
                              "Listed_Pages",
                              "Features",
                            ]}
                            values={formData.features.Tracking_data_collection}
                            onChange={(updates) =>
                              setFormData((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  Tracking_data_collection: updateFeatureGroup(
                                    prev.features.Tracking_data_collection,
                                    updates,
                                    [
                                      "Cookies",
                                      "Social_Tags",
                                      "Linked_Pages",
                                      "Listed_Pages",
                                    ]
                                  ),
                                },
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="col-12 mb-3">
                          <CheckboxGroup
                            groupLabel="Web Crawling & Configuration"
                            items={[
                              "Crawl_Rules",
                              "Email_Configuration",
                              "Archives",
                              "Carbon_Footprint",
                              "Headers",
                              "Robot_Txt",
                              "Sitemap",
                              "The_Harvester",
                              "Threats",
                              "Mail_Config",
                            ]}
                            values={
                              formData.features.Web_Crawling_configuration
                            }
                            onChange={(updates) =>
                              setFormData((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  Web_Crawling_configuration:
                                    updateFeatureGroup(
                                      prev.features.Web_Crawling_configuration,
                                      updates,
                                      [
                                        "Crawl_Rules",
                                        "Email_Configuration",
                                        "Archives",
                                        "Carbon_Footprint",
                                        "Headers",
                                        "Robot_Txt",
                                        "Sitemap",
                                        "The_Harvester",
                                        "Threats",
                                      ]
                                    ),
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-12 mb-3">
                          <CheckboxGroup
                            groupLabel="Domain & DNS Information"
                            items={[
                              "Associated_Hosts",
                              "DNS_Records",
                              "DNS_Security_Extensions",
                              "DNS_Server",
                              "Domian_Info",
                              "TXT_Records",
                              "Whois_Lookup",
                            ]}
                            values={formData.features.Domian_DNS_Information}
                            onChange={(updates) =>
                              setFormData((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  Domian_DNS_Information: updateFeatureGroup(
                                    prev.features.Domian_DNS_Information,
                                    updates,
                                    [
                                      "Associated_Hosts",
                                      "DNS_Records",
                                      "DNS_Security_Extensions",
                                      "DNS_Server",
                                      "Domian_Info",
                                      "TXT_Records",
                                      "Whois_Lookup",
                                    ]
                                  ),
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-12 mb-3 ">
                          <CheckboxGroup
                            groupLabel="Website Security & Protection"
                            items={[
                              "Block_Detection",
                              "Firewall_Detection",
                              "Malware_and_Phishing_Detection",
                              "Security",
                              "HTTP_Security_Features",
                              "HTTP_Strict_Transport_Security",
                              "SSL_Chain",
                              "SSL_Info",
                              "TLS_Cipher_Suites",
                            ]}
                            values={
                              formData.features.Website_Security_protection
                            }
                            onChange={(updates) =>
                              setFormData((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  Website_Security_protection:
                                    updateFeatureGroup(
                                      prev.features.Website_Security_protection,
                                      updates,
                                      [
                                        "Block_Detection",
                                        "Firewall_Detection",
                                        "Malware_and_Phishing_Detection",
                                        "Security",
                                        "HTTP_Security_Features",
                                        "HTTP_Strict_Transport_Security",
                                        "SSL_Chain",
                                        "SSL_Info",
                                        "TLS_Cipher_Suites",
                                      ]
                                    ),
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  {!viewMode && (
                    <button
                      type="submit"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? editingSubscriptionId
                          ? "Updating..."
                          : "Adding..."
                        : editingSubscriptionId
                        ? "Update Subscription"
                        : "Add Subscription"}
                    </button>
                  )}
                  {viewMode && (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
